import { ApolloError, NetworkStatus, useQuery } from "@apollo/client";
import React, { ReactElement, useCallback, useContext, useRef } from "react";
import {
  useQueryParams,
  withDefault,
  NumberParam,
  StringParam,
  ArrayParam,
} from "use-query-params";

import {
  ThreadSortBy,
  SearchThreadsQuery,
  SearchThreadsQueryVariables,
  SearchThreadsCountQuery,
  SearchThreadsCountQueryVariables,
  GetCampaignNamesQuery,
  GetCampaignNamesQueryVariables,
  GetTexterNamesQuery,
  GetTexterNamesQueryVariables,
  ListTagsQuery,
  ListTagsQueryVariables,
  TagSortBy,
  ThreadResponseStatus,
  PhoneNumberMessageClass,
  CampaignStatus,
  ThreadInboxFilterType,
  ListSubaccountEscalationAndGeneralInboxesQuery,
  ListSubaccountEscalationAndGeneralInboxesQueryVariables,
} from "~frontend/__generated__/graphql";
import { GET_CAMPAIGN_NAMES } from "~frontend/graphql/campaigns";
import { LIST_SUBACCOUNT_ESCALATION_AND_GENERAL_INBOXES } from "~frontend/graphql/inboxes";
import { LIST_TAGS } from "~frontend/graphql/tags";
import {
  SEARCH_THREADS,
  SEARCH_THREADS_COUNT,
} from "~frontend/graphql/threads";
import { GET_TEXTER_NAMES } from "~frontend/graphql/users";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import useMousetrap from "~frontend/util/useMousetrap";

import { OptOutFilter } from "../../routes/subaccounts/threads/types";
import {
  ALL_CAMPAIGN_STATUSES,
  THREAD_SORTS,
  gqlValueForOptOutFilter,
} from "./constants";

type ListThreadsFilterAndSort = {
  sort: ThreadSortBy;
  optOutFilter: OptOutFilter;
  responseStatusFilter: ThreadResponseStatus | null;
  campaignFilter: { id: string; name: string | null }[];
  tagFilter: {
    tags: { id: string; name: string | null }[];
    filterOperation: "any" | "all";
  };
  texterFilter: { id: string; name: string | null }[];
  searchType: "message" | "contact";
  search: string;
  phoneNumberMessageClassesFilter: PhoneNumberMessageClass[];
  inboxes: {
    id: string;
    name: string | null;
    type: "escalation" | "general" | null;
  }[];
  inboxFilterType: ThreadInboxFilterType;
  campaignStatuses: CampaignStatus[];
};

type ListThreadsPagination = {
  page: number;
  pageSize: number;
};

type ThreadsContextType = {
  listThreadsFilterAndSort: ListThreadsFilterAndSort;
  setListThreadFilterAndSort: (val: ListThreadsFilterAndSort) => void;
  listThreadsPagination: ListThreadsPagination;
  setListThreadsPagination: (val: ListThreadsPagination) => void;
  threadId: string | null;
  setThreadAndContext: (
    val: string | null,
    contextCampaignId: string | null
  ) => void;
  contextCampaignId: string | null;
  setContextCampaignId: (val: string | null) => void;
  threadsList:
    | { loading: true; error: null; data: null }
    | { loading: false; error: ApolloError; data: null }
    | {
        loading: false;
        error: null;
        data: SearchThreadsQuery["searchThreads"] | null;
        reloading: boolean;
      };
  threadsCount:
    | { loading: true; error: null; data: null }
    | { loading: false; error: ApolloError; data: null }
    | {
        loading: false;
        error: null;
        data: SearchThreadsCountQuery["searchThreadsCount"] | null;
        reloading: boolean;
      };
  loadingThreads: string[];
  markThreadLoading: (threadId: string) => void;
  markThreadLoaded: (threadId: string) => void;
  handleNavShortcut: (direction: 1 | -1) => void;
};

const ThreadsContext = React.createContext<ThreadsContextType | null>(null);

export function useThreadsContext(): ThreadsContextType | null {
  return useContext(ThreadsContext);
}

export function ThreadsProvider({
  children,
}: {
  children: React.ReactNode;
}): ReactElement {
  const { subaccount } = useSubaccount()!;

  // We use useQueryParams to manage the query params for the threads list,
  // rather than individual calls to useQueryParam, so we can update multiple
  // parameters in a single call/navigation. This is important because when
  // there are unsaved changes to a thread, we use RouteLeavingGuard to
  // warn before navigation -- so we need the whole navigation to be a single
  // call use setQueryParams(), rather than a bunch of calls to setQueryParam()
  // that happen in sequence and would each trigger the RouteLeavingGuard.
  const [queryParams, setQueryParams] = useQueryParams({
    page: withDefault(NumberParam, 0),
    page_size: withDefault(NumberParam, 10),
    sort: withDefault(StringParam, THREAD_SORTS[0].value),
    opt_out_status: withDefault(StringParam, "ANY"),
    response_status: withDefault(StringParam, null),
    campaign_ids: withDefault(ArrayParam, []),
    tag_ids: withDefault(ArrayParam, []),
    tag_filter_operation: withDefault(StringParam, "any"),
    texter_ids: withDefault(ArrayParam, []),
    search_type: withDefault(StringParam, "message"),
    search: withDefault(StringParam, ""),
    messaging_strategies: withDefault(ArrayParam, []),
    inbox_ids: withDefault(ArrayParam, []),
    inbox_filter_type: withDefault(StringParam, ThreadInboxFilterType.Current),
    campaign_statuses: withDefault(ArrayParam, ALL_CAMPAIGN_STATUSES),
    thread: StringParam,
    context: StringParam,
  });

  const campaignIds = queryParams.campaign_ids.filter(
    campaignId => !!campaignId
  ) as string[];
  const texterIds = queryParams.texter_ids.filter(
    texterId => !!texterId
  ) as string[];

  const getCampaignNamesResult = useQuery<
    GetCampaignNamesQuery,
    GetCampaignNamesQueryVariables
  >(GET_CAMPAIGN_NAMES, {
    variables: {
      subaccountId: subaccount.id,
      campaignIds,
      limit: campaignIds.length,
    },
    skip: campaignIds.length === 0,
  });

  const campaignIdToName: { [id: string]: string } =
    getCampaignNamesResult.data?.listCampaigns.campaigns.reduce(
      (acc, campaign) => ({ ...acc, [campaign.id]: campaign.name }),
      {}
    ) ?? {};

  const getTexterNamesResult = useQuery<
    GetTexterNamesQuery,
    GetTexterNamesQueryVariables
  >(GET_TEXTER_NAMES, {
    variables: {
      subaccountId: subaccount.id,
      texterIds,
      limit: texterIds.length,
    },
    skip: texterIds.length === 0,
  });

  const texterIdToName: { [id: string]: string } =
    getTexterNamesResult.data?.listSubaccountUsers.users.reduce(
      (acc, user) => ({ ...acc, [user.id]: user.fullName }),
      {}
    ) ?? {};

  const listTagsResult = useQuery<ListTagsQuery, ListTagsQueryVariables>(
    LIST_TAGS,
    {
      variables: {
        input: {
          sort: TagSortBy.NameAsc,
          subaccountId: subaccount.id,
        },
      },
      fetchPolicy: "network-only",
    }
  );

  const tagIds = queryParams.tag_ids.filter(t => !!t) as string[];

  const tagIdToName: { [id: string]: string } =
    listTagsResult.data?.listTags.tags.reduce(
      (acc, tag) => ({
        ...acc,
        [tag.id]: tag.name,
      }),
      {}
    ) ?? {};

  const tagFilterOperation =
    queryParams.tag_filter_operation === "all" ? "all" : "any";
  if (tagFilterOperation !== queryParams.tag_filter_operation) {
    console.warn(
      `Invalid tag filter operation ${queryParams.tag_filter_operation}`
    );
  }

  const inboxIds = queryParams.inbox_ids.filter(e => !!e) as string[];

  const listInboxes = useQuery<
    ListSubaccountEscalationAndGeneralInboxesQuery,
    ListSubaccountEscalationAndGeneralInboxesQueryVariables
  >(LIST_SUBACCOUNT_ESCALATION_AND_GENERAL_INBOXES, {
    variables: { subaccountId: subaccount.id },
  });

  const inboxIdToNameAndType: {
    [id: string]: { name: string | null; type: "escalation" | "general" };
  } = {
    ...(listInboxes.data?.listSubaccountEscalationInboxes.reduce(
      (acc, escalationInbox) => ({
        ...acc,
        [escalationInbox.id]: {
          name: escalationInbox.name,
          type: "escalation",
        },
      }),
      {}
    ) ?? {}),
    ...(listInboxes.data?.listSubaccountGeneralInboxes.reduce(
      (acc, generalInbox) => ({
        ...acc,
        [generalInbox.id]: {
          name: generalInbox.name,
          type: "general",
        },
      }),
      {}
    ) ?? {}),
  };

  const listThreadsFilterAndSort: ListThreadsFilterAndSort = {
    sort: queryParams.sort as ThreadSortBy,
    optOutFilter: queryParams.opt_out_status as OptOutFilter,
    responseStatusFilter:
      queryParams.response_status as ThreadResponseStatus | null,
    campaignFilter: campaignIds.map(id => ({
      id,
      name: campaignIdToName?.[id],
    })),
    tagFilter: {
      tags: tagIds.map(id => ({ id, name: tagIdToName?.[id] })),
      filterOperation: tagFilterOperation,
    },
    texterFilter: texterIds.map(id => ({ id, name: texterIdToName?.[id] })),
    searchType: queryParams.search_type as "message" | "contact",
    search: queryParams.search,
    phoneNumberMessageClassesFilter: queryParams.messaging_strategies.filter(
      messagingStrategy => !!messagingStrategy
    ) as PhoneNumberMessageClass[],
    inboxes: inboxIds.map(id => ({
      id,
      name: inboxIdToNameAndType[id]?.name,
      type: inboxIdToNameAndType[id]?.type,
    })),
    inboxFilterType: queryParams.inbox_filter_type as ThreadInboxFilterType,
    campaignStatuses: queryParams.campaign_statuses as CampaignStatus[],
  };

  const setListThreadFilterAndSort = useCallback(
    (newOptions: ListThreadsFilterAndSort) =>
      setQueryParams(
        {
          page: 0,
          page_size: 10,
          sort: newOptions.sort,
          opt_out_status: newOptions.optOutFilter,
          response_status: newOptions.responseStatusFilter,
          campaign_ids: newOptions.campaignFilter.map(({ id }) => id),
          texter_ids: newOptions.texterFilter.map(({ id }) => id),
          tag_ids: newOptions.tagFilter.tags.map(({ id }) => id),
          tag_filter_operation: newOptions.tagFilter.filterOperation,
          search_type: newOptions.searchType,
          search: newOptions.search,
          messaging_strategies: newOptions.phoneNumberMessageClassesFilter,
          inbox_ids: newOptions.inboxes.map(({ id }) => id),
          inbox_filter_type: newOptions.inboxFilterType,
          campaign_statuses: newOptions.campaignStatuses,
        },
        "pushIn"
      ),
    [setQueryParams]
  );

  const listThreadsPagination = {
    page: queryParams.page,
    pageSize: queryParams.page_size,
  };

  const setListThreadsPagination = useCallback(
    (newOptions: ListThreadsPagination) =>
      setQueryParams(
        {
          page: newOptions.page,
          page_size: newOptions.pageSize,
        },
        "pushIn"
      ),
    [setQueryParams]
  );

  const setThreadAndContext = useCallback(
    (threadId: string | null, contextCampaignId: string | null) =>
      setQueryParams(
        { thread: threadId, context: contextCampaignId },
        "pushIn"
      ),
    [setQueryParams]
  );

  const setContextCampaignId = useCallback(
    (campaignId: string | null) =>
      setQueryParams({ context: campaignId }, "pushIn"),
    [setQueryParams]
  );

  // This ref lets us say that we want to auto-select the first/last thread
  // after we're done loading the next page. It's used when keyboard-navigating
  // between threads across pages.
  const autoSelectThreadRef = useRef<"first" | "last" | null>(null);

  const filter = {
    isOptedOut: gqlValueForOptOutFilter(listThreadsFilterAndSort.optOutFilter),
    campaignIds:
      listThreadsFilterAndSort.campaignFilter.length > 0
        ? listThreadsFilterAndSort.campaignFilter.map(({ id }) => id)
        : null,
    campaignStatuses:
      listThreadsFilterAndSort.campaignStatuses.length > 0 &&
      ALL_CAMPAIGN_STATUSES.some(
        status => !listThreadsFilterAndSort.campaignStatuses.includes(status)
      )
        ? listThreadsFilterAndSort.campaignStatuses
        : null,
    texterIds:
      listThreadsFilterAndSort.texterFilter.length > 0
        ? listThreadsFilterAndSort.texterFilter.map(({ id }) => id)
        : null,
    contactSearch:
      listThreadsFilterAndSort.searchType === "contact" &&
      listThreadsFilterAndSort.search.length > 0
        ? listThreadsFilterAndSort.search
        : null,
    messageSearch:
      listThreadsFilterAndSort.searchType === "message" &&
      listThreadsFilterAndSort.search.length > 0
        ? listThreadsFilterAndSort.search
        : null,
    phoneNumberMessageClasses:
      listThreadsFilterAndSort.phoneNumberMessageClassesFilter.length > 0
        ? listThreadsFilterAndSort.phoneNumberMessageClassesFilter
        : null,
    responseStatus: listThreadsFilterAndSort.responseStatusFilter,
    inboxIds: listThreadsFilterAndSort.inboxes.map(({ id }) => id),
    inboxFilterType: listThreadsFilterAndSort.inboxFilterType,
    anyTagIds: null as string[] | null,
    allTagIds: null as string[] | null,
  };

  const tagFilter = listThreadsFilterAndSort.tagFilter;

  if (tagFilter.tags.length > 0) {
    switch (tagFilter.filterOperation) {
      case "any":
        filter.anyTagIds = tagFilter.tags.map(({ id }) => id);
        break;
      case "all":
        filter.allTagIds = tagFilter.tags.map(({ id }) => id);
        break;
      default:
        throw new Error(
          `Unhandled tag filter operation type ${tagFilter.filterOperation}`
        );
    }
  }

  const searchThreadsResult = useQuery<
    SearchThreadsQuery,
    SearchThreadsQueryVariables
  >(SEARCH_THREADS, {
    variables: {
      input: {
        limit: listThreadsPagination.pageSize,
        offset: listThreadsPagination.page * listThreadsPagination.pageSize,
        filter,
        sort: listThreadsFilterAndSort.sort,
        subaccountId: subaccount.id,
      },
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      if (autoSelectThreadRef.current) {
        const threads = data.searchThreads;
        if (threads.length > 0) {
          const thread =
            threads[
              autoSelectThreadRef.current === "first" ? 0 : threads.length - 1
            ];
          setThreadAndContext(
            thread.id,
            thread.defaultContextCampaign?.id ?? null
          );
        }
        autoSelectThreadRef.current = null;
      }
    },
  });

  const searchThreadsCountResult = useQuery<
    SearchThreadsCountQuery,
    SearchThreadsCountQueryVariables
  >(SEARCH_THREADS_COUNT, {
    variables: {
      input: {
        filter,
        subaccountId: subaccount.id,
      },
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  function handleNavShortcut(direction: 1 | -1) {
    if (!searchThreadsResult.data) {
      return;
    }

    const threads = searchThreadsResult.data.searchThreads;

    const currentIdx = threads.findIndex(
      thread => thread.id === queryParams.thread
    );
    if (currentIdx === -1) {
      return;
    }

    const newIdx = currentIdx + direction;
    if (newIdx < 0) {
      if (listThreadsPagination.page > 0) {
        autoSelectThreadRef.current = "last";
        setListThreadsPagination({
          ...listThreadsPagination,
          page: listThreadsPagination.page - 1,
        });
      }
    } else if (newIdx >= threads.length) {
      const totalCount =
        searchThreadsCountResult.data?.searchThreadsCount.totalCount;
      if (
        totalCount &&
        totalCount >
          (listThreadsPagination.page + 1) * listThreadsPagination.pageSize
      ) {
        autoSelectThreadRef.current = "first";
        setListThreadsPagination({
          ...listThreadsPagination,
          page: listThreadsPagination.page + 1,
        });
      }
    } else {
      const newThread = threads[newIdx];
      setQueryParams(
        {
          thread: newThread.id,
          context: newThread.defaultContextCampaign?.id ?? null,
        },
        "pushIn"
      );
    }
  }

  useMousetrap("shift+up", () => {
    handleNavShortcut(-1);
  });

  useMousetrap("shift+down", () => {
    handleNavShortcut(1);
  });

  const [loadingThreads, setLoadingThreads] = React.useState<string[]>([]);
  const markThreadLoading = (threadId: string) =>
    setLoadingThreads(Array.from(new Set([...loadingThreads, threadId])));
  const markThreadLoaded = (threadId: string) =>
    setLoadingThreads(loadingThreads.filter(id => id !== threadId));

  return (
    <ThreadsContext.Provider
      value={{
        listThreadsFilterAndSort,
        setListThreadFilterAndSort,
        listThreadsPagination,
        setListThreadsPagination,
        threadId: queryParams.thread ?? null,
        setThreadAndContext,
        contextCampaignId: queryParams.context ?? null,
        setContextCampaignId,
        threadsList:
          searchThreadsResult.networkStatus === NetworkStatus.loading ||
          searchThreadsResult.networkStatus === NetworkStatus.setVariables
            ? { loading: true, error: null, data: null }
            : searchThreadsResult.error
              ? { loading: false, error: searchThreadsResult.error, data: null }
              : {
                  loading: false,
                  error: null,
                  data: searchThreadsResult.data!.searchThreads,
                  reloading:
                    searchThreadsResult.networkStatus === NetworkStatus.refetch,
                },
        threadsCount: searchThreadsCountResult.loading
          ? { loading: true, error: null, data: null }
          : searchThreadsCountResult.error
            ? {
                loading: false,
                error: searchThreadsCountResult.error,
                data: null,
              }
            : {
                loading: false,
                error: null,
                data: searchThreadsCountResult.data!.searchThreadsCount,
                reloading:
                  searchThreadsCountResult.networkStatus ===
                  NetworkStatus.refetch,
              },
        loadingThreads,
        markThreadLoading,
        markThreadLoaded,
        handleNavShortcut,
      }}
    >
      {children}
    </ThreadsContext.Provider>
  );
}
